import { NodeEvalFunctionType, LogicalOperatorType, NodeOperatorType, TreeEvalFunctionType } from './types';
import { isNil } from 'lodash';

const toComparableValue = (input: any): number | undefined => {
  if (isNil(input)) return undefined;

  if (input instanceof Date) {
    const time = input.getTime();
    return Number.isNaN(time) ? undefined : time;
  }

  if (typeof input === 'number') return Number.isNaN(input) ? undefined : input;
  if (typeof input !== 'string') return undefined;

  const normalizedInput = input.trim();
  if (!normalizedInput.length) return undefined;

  const numericValue = Number(normalizedInput);
  if (!Number.isNaN(numericValue)) return numericValue;

  const dateValue = new Date(normalizedInput).getTime();
  if (!Number.isNaN(dateValue)) return dateValue;

  return undefined;
};

const getComparableAnswer = (answer: any, value: any) => {
  const comparableAnswer = toComparableValue(answer);
  const comparableValue = toComparableValue(value);
  if (comparableAnswer === undefined || comparableValue === undefined) return undefined;
  return { comparableAnswer, comparableValue };
};

const equalFunc: NodeEvalFunctionType = (answer: any, value: any) => {
  const comparableResult = getComparableAnswer(answer, value);
  if (comparableResult !== undefined) return comparableResult.comparableAnswer === comparableResult.comparableValue;
  return answer === value;
};

const notEqualFunc: NodeEvalFunctionType = (answer: any, value: any) => !equalFunc(answer, value);

const greaterFunc: NodeEvalFunctionType = (answer: any, value: any) => {
  const comparableResult = getComparableAnswer(answer, value);
  return comparableResult !== undefined && comparableResult.comparableAnswer > comparableResult.comparableValue;
};

const greaterEqualFunc: NodeEvalFunctionType = (answer: any, value: any) => {
  const comparableResult = getComparableAnswer(answer, value);
  return comparableResult !== undefined && comparableResult.comparableAnswer >= comparableResult.comparableValue;
};

const lessFunc: NodeEvalFunctionType = (answer: any, value: any) => {
  const comparableResult = getComparableAnswer(answer, value);
  return comparableResult !== undefined && comparableResult.comparableAnswer < comparableResult.comparableValue;
};

const lessEqualFunc: NodeEvalFunctionType = (answer: any, value: any) => {
  const comparableResult = getComparableAnswer(answer, value);
  return comparableResult !== undefined && comparableResult.comparableAnswer <= comparableResult.comparableValue;
};

const emptyFunc: NodeEvalFunctionType = (answer: any) => {
  return (
    answer === null ||
    answer === undefined ||
    (typeof answer === 'string' && !answer.trim().length) ||
    (Array.isArray(answer) && !answer.length)
  );
};

const notEmptyFunc: NodeEvalFunctionType = (answer: any) => {
  return !emptyFunc(answer);
};

const anyofFunc: NodeEvalFunctionType = (answer: string[] | string, value: string[] | string) => {
  if (Array.isArray(value) && Array.isArray(answer)) return value.some((x) => answer.includes(x));

  if (Array.isArray(value) && !Array.isArray(answer)) return value.includes(answer);

  if (!Array.isArray(value) && Array.isArray(answer)) return answer.includes(value);

  return value === answer;
};

const containsFunc: NodeEvalFunctionType = (answer: string[] | string, value: string) => anyofFunc(answer, value);

export const nodeOperatorFuncResolver: Record<NodeOperatorType, NodeEvalFunctionType> = {
  '===': equalFunc,
  '!==': notEqualFunc,
  '>': greaterFunc,
  '>=': greaterEqualFunc,
  '<': lessFunc,
  '<=': lessEqualFunc,
  empty: emptyFunc,
  notempty: notEmptyFunc,
  anyof: anyofFunc,
  contains: containsFunc,
};

const andFunc: NodeEvalFunctionType = (operandOne: boolean, operandTwo: boolean) => {
  return operandOne && operandTwo;
};

const orFunc: NodeEvalFunctionType = (operandOne: boolean, operandTwo: boolean) => {
  return operandOne || operandTwo;
};

export const logicalOperatorFuncResolver: Record<LogicalOperatorType, TreeEvalFunctionType> = {
  '&&': andFunc,
  '||': orFunc,
};
